import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Box, Button, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Lockup from './components/Lockup';
import ToastBar from './components/ToastBar';
import ViewToggle, { TOGGLE_EASE, TOGGLE_MOVE_MS, type ViewToggleHandle } from './components/ViewToggle';
import HeaderAction from './components/HeaderAction';
import SavedButton from './components/SavedButton';
import type { ChromeSize } from './components/ChromePill';
import {
  CITY_PILL_W,
  GROUP_GAP,
  LAYOUT_ITEMS,
  SAVED_BUTTON_MAX_W,
  TOGGLE_BAND,
  TOGGLE_HEADER_SCALE,
  groupSpan,
  toggleBandPx,
  type Layout,
  type Scope,
} from './components/viewToggleMetrics';
import AboutModal from './features/about/AboutModal';
import CityChooser from './features/city/CityChooser';
import CityPill from './features/city/CityPill';
import { CITIES, switchCity, useCity } from './features/city/useCity';
import EventDetailSheet, { type CalloutLayout } from './features/explore/EventDetailSheet';
import EventList from './features/explore/EventList';
import ResizableSidebar from './components/ResizableSidebar';
import RightColumn from './components/RightColumn';
import SidebarToggle from './components/SidebarToggle';
import {
  CentreColumn,
  PhoneToggleDock,
  // AccountLinkSpot, (the Account link the view toggle's third item replaced)
  // PreviewLinks, (the retired Log in / Sign up)
  RIGHT_SIDEBAR_X,
  RightSidebar,
  RightSidebarBody,
  SidebarTop,
  StripLinks,
  StripLockup,
  StripSiteLinks,
  TOP_STRIP_H,
  ToggleDock,
  ToggleSpot,
  TopStrip,
} from './components/Frame';
import Sidebar from './features/explore/Sidebar';
import ReportDialog from './features/report/ReportDialog';
import MobileNotice from './features/mobile/MobileNotice';
import CalendarView, { type ScheduleFocus } from './features/schedule/CalendarView';
import SavedMenu from './features/schedule/SavedMenu';
import ShareCalendarDialog from './features/share/ShareCalendarDialog';
import MapChrome from './features/map/MapChrome';
import type { MapViewHandle } from './features/map/MapView';
import type { MapFilterField } from './features/travel/MapFilterButton';
import { decodeSavedList } from './shared/ics/savedList';
import {
  activeFilterCount,
  applyFilters,
  collapseSeries,
  facetCounts,
  filterKey as signatureOf,
  seriesDates,
} from './features/explore/filters';
import { isLegacySaved, layoutFromSearch, sharedFromSearch } from './features/explore/urlState';
import { useExplore } from './features/explore/useExplore';
import ResultsNotes from './features/explore/ResultsNotes';
import DistanceSection from './features/travel/DistanceSection';
import HomePicker from './features/travel/HomePicker';
import { travelNote } from './features/travel/TravelNote';
import { travelBand, withinPhrase } from './features/travel/estimate';
import { homePoint, useCurrentLocation, useHome, type AddressOrigin, type Home } from './features/travel/home';
import { DEFAULT_WITHIN, type TravelMode } from './features/travel/walkshed';
import { BRAND } from './shared/theme/paletteDark';
import { FONTS } from './shared/theme/typography';
import { useRsvp, type RsvpNotify, type RsvpStatus } from './shared/useRsvp';
import PhoneScheduleCount from './features/schedule/PhoneScheduleCount';
import { useCityData } from './shared/useCityData';
import { useToasts } from './shared/useToasts';
import { isPastEvent, useNow } from './shared/pastEvents';
import { useHiddenEvents } from './shared/useHiddenEvents';
import EventEditDialog from './features/edit/EventEditDialog';
import {
  applyEdit,
  useEventEdits,
  type EditScope,
  type EventFields,
} from './features/edit/eventEdits';
import { clearScrollMemory, useScrollMemory } from './shared/useScrollMemory';
import { useMobileNotice } from './shared/useIsPhone';
import { useIsMobile } from './shared/useIsMobile';
import { usePhoneChrome } from './shared/usePhoneChrome';
import FilterPanel, { type FilterPanelHandle } from './features/explore/FilterPanel';
import PageSwipe, { type PageSwipeHandle } from './components/PageSwipe';
import { startsOnOwnDrag, useSwipeGesture } from './shared/useSwipeGesture';
import { useCalloutTrail } from './features/explore/useCalloutTrail';
import { useSidebars } from './features/explore/useSidebars';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { localDate, type Opportunity } from './shared/opportunity';
import { EVENT_TYPES, ROLES, TOPICS } from './shared/taxonomy';
import {
  HIDE_MS,
  FEEDBACK_URL,
  LOGO_W,
  SWIPE_HINT_KEY,
  LOCKUP_IN_SIDEBAR,
  LOCKUP_IN_STRIP,
} from './shared/utils/constants';
import {
  Shell,
  Content,
  Header,
  HeaderToggle,
  Panes,
  Pane,
  Centered,
  PaneWrap,
  Fade,
  TextLink,
  ResultsBar,
} from './shared/SharedComponents';

// Lazy, so Leaflet stays out of the main bundle until the Map layout is first shown.
const MapView = lazy(() => import('./features/map/MapView'));

/** Cards drawn at a time: the list grows by this many as its end comes into view. */
const PAGE = 40;
const NOOP = () => {};
const NO_IDS: ReadonlySet<string> = new Set();

/** The address without a shared calendar's `ids=` and `city=`: leaving it for one's own. */
function withoutShared(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('ids');
  p.delete('city');
  const s = p.toString().replace(/%2C/gi, ',');
  return s ? `?${s}` : '';
}

/**
 * The top-right corner of a column holds the view's button and the right
 * sidebar's toggle, over the results bar, which is centred with the tickets.
 * Where the two meet, pad the bar's right side so its text wraps short of the
 * button instead of running under it. A ref for that corner's `ToggleSpot`.
 */
function keepResultsBarClear(spot: HTMLElement | null) {
  const wrap = spot?.parentElement;
  if (!spot || !wrap) return;
  const fit = () => {
    const bar = wrap.querySelector<HTMLElement>('[data-results-bar]:not([data-results-bar="map"])');
    if (!bar) return;
    bar.style.paddingRight = '';
    const overlap = bar.getBoundingClientRect().right - spot.getBoundingClientRect().left + 12;
    if (overlap > parseFloat(getComputedStyle(bar).paddingRight)) bar.style.paddingRight = `${Math.ceil(overlap)}px`;
  };
  const observer = new ResizeObserver(fit);
  observer.observe(spot);
  observer.observe(wrap);
  fit();
  return () => observer.disconnect();
}

/** Straight-line km between two points: close enough to say "not in this city". */
function kmBetween([a, b]: [number, number], [c, d]: [number, number]): number {
  const rad = Math.PI / 180;
  const x = (d - b) * rad * Math.cos(((a + c) / 2) * rad);
  const y = (c - a) * rad;
  return Math.hypot(x, y) * 6371;
}

export default function App() {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  /** The phone layout. Narrower than the narrow one; see `useIsMobile`. */
  const isMobile = useIsMobile();
  /** A phone, as against a narrow window: the bottom toggle and the new chrome. */
  const phoneChrome = usePhoneChrome();
  /**
   * Below 900px, without a phone's chrome, the header holds the lockup, the
   * centred group (city pill, layout pill, Saved) and the view's own button on
   * one line. The wordmark gives way to the mark below 760px; under 600px,
   * where the group does not fit beside the other two, it takes a row of its own.
   */
  const headerWordmark = useMediaQuery('(min-width: 760px)');
  const stackedGroup = useMediaQuery('(max-width: 599.95px)');
  /** The header's group from 900px up: how far it reaches either side of its anchor, the layout pill's centre. */
  const span = useMemo(() => groupSpan(), []);
  const toggleW = span.left + span.right;

  const toasts = useToasts();
  /** The current city's file: every listing, the taxonomy, the neighborhoods. */
  const city = useCityData();
  /** The user's own corrections, applied over the listings everywhere they appear. */
  const edits = useEventEdits();
  /** Events marked Not Interested. */
  const hidden = useHiddenEvents();

  // Phones only, once per browser.
  const mobileNotice = useMobileNotice();
  /** False on a first visit that arrived by a link: "Where are you?" asks first. */
  const { chosen: cityChosen, city: cityKey, setCity: chooseCity } = useCity();
  /** Where travel is measured from: the stored home, and the device once asked (contract §11). */
  const homeStore = useHome(cityKey);
  const here = useCurrentLocation(cityKey);
  const homeAt = useMemo(() => homePoint(homeStore.home), [homeStore.home]);
  /** A place searched in the map's popover: measured from for this session only, never stored (Session 9). */
  const [addressOrigin, setAddressOrigin] = useState<AddressOrigin | null>(null);

  // Each scope's list keeps its scroll position across a trip to another
  // scope or layout, which unmounts it.
  const listScroll = {
    explore: useScrollMemory('explore:list'),
    saved: useScrollMemory('saved:list'),
  };

  // --- routes (contract §4, §10) ----------------------------------------------
  // `/explore` and `/saved` are the two scopes, `?view=` the layout; `/e/:id` is
  // Explore with that event's callout open, the shareable link. App stays
  // mounted across all of them.
  const location = useLocation();
  const navigate = useNavigate();
  const routeScope: Scope = location.pathname.startsWith('/saved') ? 'saved' : 'explore';
  const linkedId = matchPath('/e/:id', location.pathname)?.params.id ?? null;
  const [scope, setScope] = useState<Scope>(routeScope);
  // Back and forward move between the paths; the scope follows. Only a change
  // of path moves it, never a path that has yet to catch up: the router
  // applies a navigation in a transition, so the render that sets the scope
  // still sees the old path, and reading that as a change would undo the tap.
  const [seenRoute, setSeenRoute] = useState(routeScope);
  if (seenRoute !== routeScope) {
    setSeenRoute(routeScope);
    setScope(routeScope);
  }
  /**
   * The layout is read from the address once, and from then on written to it
   * beside the filters. Like the filters, it is state rather than the address:
   * closing a callout goes Back to the entry under `/e/:id`, whose query is as
   * old as the callout, and the layout must not go back with it.
   */
  const [layout, setLayout] = useState<Layout>(() => layoutFromSearch(location.search));
  /** The callout's event, and its history. */
  const {
    detail,
    show: showDetail,
    close: closeDetail,
    update: updateDetail,
    back: detailBack,
    forward: detailForward,
  } = useCalloutTrail();
  /** "Go to Saved" from a toast or the callout: the month and day the calendar lands on. */
  const [scheduleFocus, setScheduleFocus] = useState<ScheduleFocus | null>(null);
  /** The callout was opened from a pin on a phone's map: half up first, the map still in view (contract §15). */
  const [detailHalf, setDetailHalf] = useState(false);
  /** Share my calendar, the Calendar layout's header button (contract §18). */
  const [shareOpen, setShareOpen] = useState(false);
  /** The map's popover, asked for from the List's sort toggle; see `HeaderAction`. */
  const [glanceAsk, setGlanceAsk] = useState<MapFilterField | null>(null);
  const [reporting, setReporting] = useState<Opportunity | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  /** Search and Filter, below 900px: the panel behind the tab on the left edge. */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** Explore's two sidebars from 900px up, and the view toggle's place. */
  const {
    calloutFits,
    rightHolds,
    rightOpen,
    filtersShown,
    leftFraction,
    maxLeftFraction,
    filtersEdge,
    toggleX,
    setLeftShare,
    toggleLeft,
    toggleRight,
  } = useSidebars({ isNarrow, callout: detail !== null, onCloseCallout: closeDetail, span });

  /** How the callout is drawn; see `CalloutLayout`. */
  const calloutLayout: CalloutLayout = !isNarrow
    ? calloutFits
      ? 'pane'
      : 'over'
    : detailHalf
      ? 'half'
      : isMobile
        ? 'sheet'
        : 'over';

  const [editing, setEditing] = useState<Opportunity | null>(null);

  const header = useRef<HTMLElement | null>(null);

  /** Bumped once to demonstrate the swipe on the first card; see where it fires, below. */
  const [swipeHint, setSwipeHint] = useState(0);
  const hinted = useRef(false);

  /** Every listing, with the user's edits applied. */
  const editedById = useMemo(() => {
    const map = new Map<string, Opportunity>();
    for (const [id, edit] of Object.entries(edits.edits)) {
      const listing = city.byId.get(id);
      if (listing) map.set(id, applyEdit(listing, edit));
    }
    return map;
  }, [edits.edits, city.byId]);
  const byId = useMemo(
    () => (editedById.size ? new Map([...city.byId].map(([id, e]) => [id, editedById.get(id) ?? e])) : city.byId),
    [city.byId, editedById],
  );

  /**
   * Going and Maybe, per city. Going's toast offers the way to Saved, as "Add
   * to Schedule" did to My Schedule.
   */
  const notifyRsvp = useCallback<RsvpNotify>(
    (message, status, id) => {
      const event = byId.get(id);
      toasts.show(
        message,
        status === 'going' && event ? { kind: 'goToSchedule', eventId: id, dayKey: localDate(event) } : undefined,
      );
    },
    [byId, toasts],
  );
  const rsvp = useRsvp(notifyRsvp);

  /** The city's clock, moved on when an event on screen ends. */
  const now = useNow(city.all, city.file?.timezone);

  // --- Explore's list ------------------------------------------------------------

  const explore = useExplore({
    file: city.file,
    all: city.all,
    hiddenIds: hidden.ids,
    rsvpIds: rsvp.allIds,
    editedById,
    home: homeAt,
    current: here.point,
    address: addressOrigin,
    homeMode: homeStore.home?.mode,
    now,
    scope,
    layout,
  });
  const { filters, setFilters } = explore;

  // --- a shared calendar, /saved?ids=…&city=… (contract §18) ----------------------
  // Saved, over the ids in the link rather than the visitor's own, read-only
  // and in the Calendar layout, until "Save all to mine" or a way out.
  const shared = routeScope === 'saved' ? sharedFromSearch(location.search) : null;
  const sharedIds = shared?.ids ?? null;
  const sharedList = useMemo(() => (sharedIds ? decodeSavedList(sharedIds) : null), [sharedIds]);
  const sharedCtx = useMemo(
    () => (sharedList ? { ...explore.ctx, rsvpIds: new Set(sharedList.keys()), hiddenIds: NO_IDS } : null),
    [sharedList, explore.ctx],
  );
  /** "Save all to mine" was pressed: Calendar, once the shared ids are out of the address. */
  const [calendarAfterShared, setCalendarAfterShared] = useState(false);
  useEffect(() => {
    if (!calendarAfterShared || sharedIds) return;
    setCalendarAfterShared(false);
    setLayout('calendar');
  }, [calendarAfterShared, sharedIds]);
  const sharedResult = useMemo(
    () => (sharedCtx ? applyFilters(city.all, { ...filters, savedOnly: true }, sharedCtx) : null),
    [sharedCtx, city.all, filters],
  );
  /** The filters' counts over the link's events, not the visitor's own Saved. */
  const sharedCounts = useMemo(
    () => (sharedCtx ? facetCounts(city.all, { ...filters, savedOnly: true }, sharedCtx) : null),
    [sharedCtx, city.all, filters],
  );
  // The link names its city: a first visit takes it rather than asking, and a
  // visitor elsewhere is moved to it. Never a city that isn't live.
  const sharedCity = shared?.city ?? null;
  useEffect(() => {
    const target = CITIES.find((c) => c.key === sharedCity && c.enabled);
    if (!target) return;
    if (!cityChosen) chooseCity(target.key);
    else if (target.key !== cityKey) switchCity(target.key);
    // On arrival with a link, not on every change of city.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedCity]);

  // --- travel: home, current location, mode --------------------------------------

  /** The first home turns the filter on at 30 minutes; a new one keeps whatever the slider says. */
  const setHome = useCallback(
    (choice: Omit<Home, 'mode'>) => {
      const first = !homeStore.home;
      homeStore.setHome({ ...choice, mode: filters.mode });
      setFilters((f) => ({ ...f, origin: 'home', withinMinutes: first ? (f.withinMinutes ?? DEFAULT_WITHIN) : f.withinMinutes }));
    },
    [homeStore, filters.mode, setFilters],
  );
  const setMode = useCallback(
    (mode: TravelMode) => {
      explore.setMode(mode);
      homeStore.setMode(mode);
    },
    [explore, homeStore],
  );
  /** Measure from a searched place, for this session: the home is untouched until Set home. */
  const setAddress = useCallback(
    (place: AddressOrigin) => {
      setAddressOrigin(place);
      setFilters((f) => ({ ...f, origin: 'address', withinMinutes: f.withinMinutes ?? DEFAULT_WITHIN }));
    },
    [setFilters],
  );
  /** Asked once; a refusal falls back to home and says so. */
  const locate = useCallback(async () => {
    if (await here.request()) {
      setFilters((f) => ({ ...f, origin: 'current', withinMinutes: f.withinMinutes ?? DEFAULT_WITHIN }));
    } else {
      setFilters((f) => ({ ...f, origin: 'home' }));
      toasts.show(
        homeStore.home
          ? `Couldn\u2019t get your location, so travel times are from ${homeStore.home.label}.`
          : 'Couldn\u2019t get your location. Set a home to filter by travel time.',
      );
    }
  }, [here, setFilters, toasts, homeStore.home]);
  // A link that says `from=current`: ask on arrival, as the link means.
  const askedOnArrival = useRef(false);
  useEffect(() => {
    if (askedOnArrival.current || filters.origin !== 'current' || here.status !== 'idle') return;
    askedOnArrival.current = true;
    void locate();
  }, [filters.origin, here.status, locate]);

  /**
   * MCW's incremental rendering: the list draws 40 cards and grows by 40 as the
   * end comes into view. Back to 40 whenever the filters change.
   */
  const [shown, setShown] = useState({ key: '', count: PAGE });
  const signature = signatureOf(filters);
  const drawn = shown.key === signature ? shown.count : PAGE;
  /**
   * What each scope draws. Both, because a phone's page swipe shows the other
   * scope beside this one; it is drawn a first page at a time.
   */
  const scopes = useMemo(() => {
    const of = (s: Scope) => {
      const { cards, result } =
        s === 'saved' && sharedResult ? { result: sharedResult, cards: collapseSeries(sharedResult.items) } : explore.byScope[s];
      /** Matches with nowhere to measure to: the list's "Distance unknown" group, one card per series. */
      const unknownCards = collapseSeries(result.unknownDistance);
      return {
        cards,
        leads: cards.map((c) => c.lead),
        unknownLeads: unknownCards.map((c) => c.lead),
        /** The map's: it draws what it can and counts the rest in its corner note. */
        mapCards: unknownCards.length ? [...cards, ...unknownCards] : cards,
        /** Per series lead: its later matching dates, for "+ 11 more dates" (Session 2). */
        moreDates: new Map(cards.filter((c) => c.more.length).map((c) => [c.lead.id, c.more.length])),
        /** Every matching date, not one per series: what the calendar draws. */
        dates: result.unknownDistance.length ? [...result.items, ...result.unknownDistance] : result.items,
        suggestion: s === 'saved' && sharedResult ? null : explore.byScope[s].suggestion,
      };
    };
    return { explore: of('explore'), saved: of('saved') };
  }, [explore.byScope, sharedResult]);
  const visible = useMemo(
    () => ({
      explore: scopes.explore.leads.slice(0, scope === 'explore' ? drawn : PAGE),
      saved: scopes.saved.leads.slice(0, scope === 'saved' ? drawn : PAGE),
    }),
    [scopes, scope, drawn],
  );
  const loadMore = useCallback(() => setShown({ key: signature, count: drawn + PAGE }), [signature, drawn]);

  /** "N of M": every matching card, and every listing in the file. */
  const shownCount = explore.cards.length;
  const poolCount = city.all.length;

  /** The wordmark behaves like a home link: Explore's list, no filters, scrolled to top. */
  const exploreList = listScroll.explore.node;

  /** "within 30 min by transit of home", after the count, while the distance filter is on. */
  const withinText =
    explore.origin && filters.withinMinutes != null
      ? withinPhrase(filters.withinMinutes, filters.mode, explore.origin.kind, {
          addressLabel: addressOrigin?.label,
          times: explore.travel.times,
        })
      : '';
  /** Nearest first's headings: travel-time bands, where Soonest first has days. The filter's own minutes. */
  const times = explore.travel.times;
  const nearestGroups = useMemo(
    () =>
      filters.sort === 'nearest' && times
        ? (event: Opportunity) => travelBand(times.minutes(event), times.mode)
        : undefined,
    [filters.sort, times],
  );
  /** The line after a card's neighborhood: "25 min by transit". */
  const travelFor = useCallback((event: Opportunity) => travelNote(times, event, city.file), [times, city.file]);
  const resetToHome = useCallback(() => {
    explore.resetAll();
    closeDetail();
    setReporting(null);
    setEditing(null);
    setAboutOpen(false);
    setScope('explore');
    setLayout('list');
    // `/e/:id` goes back to Explore as its callout closes; only Saved has to be left by hand.
    if (routeScope === 'saved') navigate('/explore', { replace: true });
    clearScrollMemory();
    exploreList.current?.scrollTo({ top: 0 });
  }, [explore, closeDetail, routeScope, navigate, exploreList]);

  // --- event actions -------------------------------------------------------

  const openDetail = useCallback((event: Opportunity) => {
    showDetail(event);
    setDetailHalf(false);
  }, [showDetail]);

  /** Going and Maybe are toggles: tapping the active one clears it. */
  const toggleRsvp = useCallback(
    (id: string, status: RsvpStatus) => {
      const title = byId.get(id)?.name ?? 'This event';
      rsvp.setStatus(id, rsvp.statusOf(id) === status ? null : status, title);
    },
    [byId, rsvp],
  );

  // --- Not Interested ------------------------------------------------------

  /** Cards playing their hide animation, and the timers that finish it. */
  const [leaving, setLeaving] = useState<Set<string>>(() => new Set());
  const leaveTimers = useRef(new Map<string, number>());

  const stopLeaving = useCallback((id: string) => {
    setLeaving((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const unhideEvent = useCallback(
    (id: string) => {
      const timer = leaveTimers.current.get(id);
      if (timer != null) {
        window.clearTimeout(timer);
        leaveTimers.current.delete(id);
        stopLeaving(id);
      }
      hidden.unhide(id);
    },
    [hidden, stopLeaving],
  );

  /**
   * "Not Interested". The card fades and the cards below close the gap, and only
   * then does the event leave the list, so the animation has a card to play on.
   * Refused for events on either list; the controls explain why.
   */
  const hideEvent = useCallback(
    (id: string) => {
      const event = byId.get(id);
      if (!event || rsvp.statusOf(id)) return;
      toasts.show('Event has been hidden from your Explore page.', { kind: 'unhide', eventId: id });

      // With hidden events shown, the card stays and just changes state.
      if (filters.showHidden) {
        hidden.hide(id);
        return;
      }
      setLeaving((current) => new Set(current).add(id));
      leaveTimers.current.set(
        id,
        window.setTimeout(() => {
          leaveTimers.current.delete(id);
          hidden.hide(id);
          stopLeaving(id);
        }, HIDE_MS),
      );
    },
    [byId, rsvp, toasts, filters.showHidden, hidden, stopLeaving],
  );

  const toggleHidden = useCallback(
    (id: string) => {
      if (!hidden.isHidden(id)) {
        hideEvent(id);
        return;
      }
      unhideEvent(id);
      toasts.show('Event is back on your Explore page.');
    },
    [hidden, hideEvent, unhideEvent, toasts],
  );

  // --- Edit this event -----------------------------------------------------

  /** Every event-type word, for the edit form's drop-down, alphabetical as it has always been. */
  const formats = useMemo(() => [...EVENT_TYPES].sort(), []);

  const saveEdit = useCallback(
    (fields: EventFields, scope: EditScope) => {
      const original = editing ? city.byId.get(editing.id) : undefined;
      setEditing(null);
      if (!original) return;

      const stored = edits.save(original, fields, scope);
      const updated = stored ? applyEdit(original, stored) : original;
      updateDetail(updated);

      if (scope === 'me') {
        // The toast says Saved, so it has to be there, and a saved event can't
        // also be hidden.
        if (hidden.isHidden(original.id)) unhideEvent(original.id);
        if (rsvp.statusOf(original.id) !== 'going') {
          rsvp.setStatus(original.id, 'going', updated.name);
        }
        toasts.show('Changes saved. You’re going, so it’s in Saved.');
      } else {
        toasts.show('Changes submitted for review.');
      }
    },
    [editing, city.byId, edits, hidden, unhideEvent, rsvp, toasts, updateDetail],
  );

  const revertEdit = useCallback(
    (id: string) => {
      edits.revert(id);
      const original = city.byId.get(id);
      if (original) updateDetail(original);
      toasts.show('Your edits to this event were removed.');
    },
    [edits, city.byId, toasts, updateDetail],
  );


  /**
   * Everything opened over one page, closed. Below 900px a sheet belongs to the
   * page it was opened from, and the toggle is now reachable while one is up:
   * nothing opened on one page may be left floating over another.
   */
  const closeSheets = useCallback(() => {
    closeDetail();
    setFiltersOpen(false);
  }, [closeDetail]);

  /** Explore or Saved: the same filters, over everything or over Going and Maybe. */
  const changeScope = useCallback(
    (s: Scope) => {
      setScope(s);
      // Replace, so switching doesn't fill the history. The query — the
      // filters and the layout — belongs to both.
      // A shared calendar's ids stay behind: either way is out of it.
      navigate({ pathname: s === 'saved' ? '/saved' : '/explore', search: withoutShared(location.search) }, { replace: true });
      if (isNarrow) closeSheets();
    },
    [isNarrow, closeSheets, navigate, location.search],
  );

  /**
   * List, Calendar or Map, in whichever scope. Anything open stays open: a
   * callout or the filters belong to the scope, and the scope has not changed.
   */
  const changeLayout = useCallback((l: Layout) => setLayout(l), []);

  // A link from before the Saved pill: `/explore?saved=1` is `/saved` now.
  useEffect(() => {
    if (location.pathname === '/explore' && isLegacySaved(location.search)) changeScope('saved');
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- the shareable link, /e/:id (Session 2) --------------------------------
  // The address follows the callout on Explore: opening one pushes /e/:id, so
  // Back closes it; moving to another event replaces it, so the trail is one
  // step; closing it goes back. And the callout follows the address: Back
  // closes it, Forward reopens it, and a link opened directly lands on Explore
  // with the callout up (an unknown id says so and falls back to /explore).
  const detailId = detail?.id ?? null;
  const lastDetailId = useRef<string | null>(null);
  const lastLinkedId = useRef<string | null>(null);
  const arrived = useRef(false);
  useEffect(() => {
    if (!city.file) return;
    const search = location.search;
    if (linkedId && linkedId !== detailId && linkedId !== lastLinkedId.current) {
      const event = byId.get(linkedId);
      if (!event) {
        toasts.show("That event isn't listed any more");
        navigate({ pathname: '/explore', search }, { replace: true });
      } else {
        if (!arrived.current) {
          // Opened from outside: put Explore under it, so Back closes the callout.
          navigate({ pathname: '/explore', search }, { replace: true });
          navigate({ pathname: `/e/${linkedId}`, search }, { state: { callout: true } });
          // Bring its card into view once the list has drawn.
          window.setTimeout(() => {
            listScroll.explore.node.current
              ?.querySelector(`[data-event-id="${CSS.escape(linkedId)}"]`)
              ?.scrollIntoView({ block: 'center' });
          }, 200);
        }
        showDetail(event);
        setDetailHalf(false);
      }
    } else if (!linkedId && detailId && lastLinkedId.current === detailId) {
      closeDetail();
    }
    arrived.current = true;
    lastLinkedId.current = linkedId;
    // Only when the address changes (or the file arrives), not when the callout does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId, city.file]);
  useEffect(() => {
    if (!city.file || scope !== 'explore') return;
    const search = location.search;
    if (detailId && linkedId !== detailId) {
      if (linkedId) navigate({ pathname: `/e/${detailId}`, search }, { replace: true, state: location.state });
      else navigate({ pathname: `/e/${detailId}`, search }, { state: { callout: true } });
      lastLinkedId.current = detailId;
    } else if (!detailId && linkedId && lastDetailId.current === linkedId) {
      lastLinkedId.current = null;
      if ((location.state as { callout?: boolean } | null)?.callout) navigate(-1);
      else navigate({ pathname: '/explore', search }, { replace: true });
    }
    lastDetailId.current = detailId;
    // Only when the callout changes, not when the address does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailId, city.file]);

  /** Copy link: the event's /e/:id, under the app's base. */
  const copyLink = useCallback(
    (event: Opportunity) => {
      const url = `${window.location.origin}${import.meta.env.BASE_URL}e/${event.id}`;
      navigator.clipboard.writeText(url).then(
        () => toasts.show('Link copied'),
        () => toasts.show(`Copy this link: ${url}`),
      );
    },
    [toasts],
  );

  /**
   * The layout pill is painted rather than rendered: a tap has to move its
   * highlight on the next frame, and the layout it switches to can take most
   * of a second to render behind it.
   */
  const layoutRef = useRef<ViewToggleHandle | null>(null);

  /** Saved, the button: to the other scope, keeping the layout and the filters. Out of a shared calendar, to one's own. */
  const toggleSaved = useCallback(() => {
    changeScope(scope === 'saved' && !shared ? 'explore' : 'saved');
  }, [changeScope, scope, shared]);

  /**
   * A tap on the layout pill. The pill commits and paints first, and the view
   * follows two frames later, so the slide is already running by the time
   * React renders the page behind it.
   */
  const tapLayout = useCallback(
    (l: Layout) => {
      if (l === layout) return;
      layoutRef.current?.follow(
        LAYOUT_ITEMS.findIndex((i) => i.key === l),
        TOGGLE_MOVE_MS,
        TOGGLE_EASE,
      );
      requestAnimationFrame(() => requestAnimationFrame(() => changeLayout(l)));
    },
    [layout, changeLayout],
  );

  // --- the view-specific buttons (contract §16) ------------------------------------

  /** Asking for the device's position on the way to Nearest first. */
  const [sortBusy, setSortBusy] = useState(false);
  /**
   * The List's sort toggle: Earliest ↔ Nearest, no menu. Nearest with nowhere
   * to measure from asks for the device's position; refused, it opens the
   * map's popover on the Home field.
   */
  const flipSort = useCallback(async () => {
    if (filters.sort === 'nearest') {
      setFilters((f) => ({ ...f, sort: 'soonest' }));
      return;
    }
    if (explore.ctx.origin) {
      setFilters((f) => ({ ...f, sort: 'nearest' }));
      return;
    }
    setSortBusy(true);
    const located = await here.request();
    setSortBusy(false);
    if (located) {
      setFilters((f) => ({ ...f, origin: 'current', sort: 'nearest' }));
    } else {
      toasts.show('Couldn\u2019t get your location. Set a home to sort by nearest.');
      setGlanceAsk('home');
    }
  }, [filters.sort, explore.ctx.origin, setFilters, here, toasts]);

  /** The recentre button: the map pans to the device, asking the first time. */
  const mapHandle = useRef<MapViewHandle | null>(null);
  const [recentring, setRecentring] = useState(false);
  const cityCentre = useMemo<[number, number] | null>(() => {
    const n = city.file?.neighborhoods ?? [];
    return n.length ? [n.reduce((a, x) => a + x.lat, 0) / n.length, n.reduce((a, x) => a + x.lng, 0) / n.length] : null;
  }, [city.file]);
  const recentreOn = useCallback(
    (point: [number, number]) => {
      // A device a long way off would take the map out of the city altogether.
      if (cityCentre && kmBetween(point, cityCentre) > 100) {
        toasts.show(`You\u2019re outside ${city.file?.cityShort ?? 'the city'}, so the map stays here.`);
        return;
      }
      mapHandle.current?.recenter(point);
    },
    [cityCentre, toasts, city.file],
  );
  const recentre = useCallback(async () => {
    if (here.point) {
      recentreOn(here.point);
      return;
    }
    setRecentring(true);
    if (!(await here.request())) {
      setRecentring(false);
      toasts.show('Couldn\u2019t get your location. Check that this site may use it, and try again.');
    }
  }, [here, recentreOn, toasts]);
  // The position arrives a render after the request resolves.
  useEffect(() => {
    if (!recentring || !here.point) return;
    setRecentring(false);
    recentreOn(here.point);
  }, [recentring, here.point, recentreOn]);

  /** The city file, before anything is drawn. */
  const loading = city.loading;
  const error = city.error;

  /**
   * A sheet half up over the day view: the callout, or the phone's personal
   * event form. The header slides away while one is, as My Schedule's own row of
   * buttons did, so the day has the screen above it. (MCW's; nothing opens one
   * half up in the demo.)
   */
  const formSheetOpen = false;
  const calloutPeeking =
    scope === 'saved' && layout !== 'map' && ((Boolean(detail) && calloutLayout === 'half') || formSheetOpen);
  useLayoutEffect(() => {
    const node = header.current;
    if (!node) return;
    node.style.transition = 'margin-top 380ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease';
    node.style.marginTop = calloutPeeking ? `-${node.offsetHeight}px` : '';
    node.style.opacity = calloutPeeking ? '0' : '';
    node.style.pointerEvents = calloutPeeking ? 'none' : '';
  }, [calloutPeeking]);

  // --- below 900px: the filter panel, and the two scopes side by side -------

  const pages = useRef<PageSwipeHandle | null>(null);
  const filterPanel = useRef<FilterPanelHandle | null>(null);

  /**
   * Saved is built beside Explore the first time it's needed, or shortly after
   * arrival, so that the first swipe toward it isn't also its first render.
   * (Explore is always built: a link lands on it.)
   */
  const [savedPreloaded, setSavedPreloaded] = useState(false);
  const savedReady = savedPreloaded || scope === 'saved';
  useEffect(() => {
    if (!isNarrow || loading || savedPreloaded) return;
    const timer = window.setTimeout(() => setSavedPreloaded(true), 1500);
    return () => window.clearTimeout(timer);
  }, [isNarrow, loading, savedPreloaded]);

  // Wide again: the sidebar is back, and the panel's state goes with the panel.
  useEffect(() => {
    if (!isNarrow) setFiltersOpen(false);
  }, [isNarrow]);

  /** Swipes are for fingers: on a phone, the emulator's mouse counts as one. */
  const swipeInput = useCallback(
    (e: React.PointerEvent) => e.pointerType !== 'mouse' || isMobile,
    [isMobile],
  );

  /**
   * Explore's own swipes, anywhere but on an event card or a map: right pulls
   * the filters open, left moves to Saved. One gesture, handed to whichever of
   * the two its direction asks for.
   */
  const exploreSwipeTo = useRef<'filters' | 'saved'>('filters');
  const onExploreSwipe = useSwipeGesture({
    accepts: (e) => scope === 'explore' && !filtersOpen && swipeInput(e) && !startsOnOwnDrag(e.target),
    claim: (direction) => {
      exploreSwipeTo.current = direction === 1 ? 'filters' : 'saved';
      if (direction === -1) setSavedPreloaded(true);
      return true;
    },
    move: (dx) => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.dragOpen(dx);
      else pages.current?.drag(dx);
    },
    release: (dx, velocity) => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.releaseOpen(dx, velocity);
      else pages.current?.release(dx, velocity);
    },
    cancel: () => {
      if (exploreSwipeTo.current === 'filters') filterPanel.current?.cancelOpen();
      else pages.current?.cancel();
    },
  });

  /**
   * The card swipe, performed once on the reader's behalf: on a phone, the first
   * time the events are on screen with nothing in front of them — after the
   * welcome, with the filters closed. Once per browser: it is a lesson, and a
   * lesson repeated is an animation in the way.
   */
  useEffect(() => {
    if (!isMobile || loading || error || scope !== 'explore' || layout !== 'list' || mobileNotice.open || filtersOpen) return;
    if (hinted.current) return;
    const timer = window.setTimeout(() => {
      hinted.current = true;
      try {
        if (localStorage.getItem(SWIPE_HINT_KEY)) return;
        localStorage.setItem(SWIPE_HINT_KEY, '1');
      } catch {
        // Site data blocked: show it this session and move on.
      }
      setSwipeHint((n) => n + 1);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [isMobile, loading, error, scope, layout, mobileNotice.open, filtersOpen]);

  // --- render --------------------------------------------------------------

  const sidebar = (
    <Sidebar
      filters={filters}
      topics={city.file?.taxonomy.topics ?? TOPICS}
      roles={city.file?.taxonomy.roles ?? ROLES}
      onQuery={explore.setQuery}
      onToggleDay={explore.toggleWeekday}
      onToggleSlot={explore.toggleSlot}
      onToggleRole={explore.toggleRole}
      onToggleTopic={explore.toggleTopic}
      onToggleSwitch={explore.toggleSwitch}
      onResetFacet={explore.resetFacet}
      onResetAll={explore.resetAll}
      // Session 2: counts and unknown-data notes. Saved is the Scope pill now.
      counts={sharedCounts ?? explore.counts}
      unknownExcluded={explore.result.unknownExcluded}
      onToggleShowHidden={() => setFilters((f) => ({ ...f, showHidden: !f.showHidden }))}
      onToggleShowPast={() => setFilters((f) => ({ ...f, showPast: !f.showPast }))}
      distance={
        <DistanceSection
          neighborhoods={city.file?.neighborhoods ?? []}
          home={homeStore.home}
          onSetHome={setHome}
          origin={filters.origin}
          onOrigin={explore.setOrigin}
          address={addressOrigin}
          locate={here.status}
          canLocate={here.supported}
          onLocate={locate}
          mode={filters.mode}
          onMode={setMode}
          withinMinutes={filters.withinMinutes}
          onWithin={explore.setWithin}
          counts={explore.counts.withinMinutes}
        />
      }
      onResetDistance={() => explore.setWithin(null)}
    />
  );

  /** From 900px up the lockup goes wherever there is room: the left sidebar, or beside the toggle. */
  const lockupIn = (size: string) => (
    <Lockup variant="wide" size={size} onHome={resetToHome} onAbout={() => setAboutOpen(true)} />
  );

  /**
   * The header's chrome (contract §15). The layout pill, as icons everywhere,
   * the words its tooltips where there is a pointer; the Saved button; and
   * the city pill. Hidden, not removed, over a shared calendar: its place in
   * the group holds.
   */
  const layoutPill = (fill: 'phone' | 'desktop', scale = 1) => (
    <Box sx={{ visibility: shared ? 'hidden' : undefined }}>
      <ViewToggle
        ref={layoutRef}
        label="Layout"
        value={layout}
        items={LAYOUT_ITEMS}
        icons
        tooltips={!phoneChrome}
        fill={fill}
        scale={scale}
        onSelect={tapLayout}
      />
    </Box>
  );
  const savedButton = (size: ChromeSize) => (
    <SavedButton on={scope === 'saved' && !shared} count={rsvp.allIds.size} size={size} onToggle={toggleSaved} />
  );
  /**
   * From 600px up: the city pill, the layout pill and the Saved button, with
   * fixed gaps, anchored on the layout pill's centre. The city pill and Saved
   * each get the room the frame reserves for them (`groupSpan`), so a longer
   * count never moves the pill.
   */
  const chromeGroup = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${GROUP_GAP}px` }}>
      <Box sx={{ width: CITY_PILL_W, display: 'flex', justifyContent: 'flex-end' }}>
        {cityPill('header')}
      </Box>
      {layoutPill('desktop', TOGGLE_HEADER_SCALE)}
      <Box sx={{ width: SAVED_BUTTON_MAX_W, display: 'flex' }}>{savedButton('desktop')}</Box>
    </Box>
  );
  /** The view's own button (contract §16): the map's glance, the list's sort, the calendar's Share. None over a shared calendar. */
  const headerAction = (size: ChromeSize) =>
    !shared && (
      <HeaderAction
        layout={layout}
        size={size}
        map={{
          origin: filters.origin,
          resolved: explore.origin?.kind ?? null,
          mode: filters.mode,
          minutes: filters.withinMinutes,
          onChangeMode: setMode,
          onChangeMinutes: explore.setWithin,
          onChangeOrigin: explore.setOrigin,
          home: homeStore.home,
          onSetHome: setHome,
          address: addressOrigin,
          onSetAddress: setAddress,
          locate: here.status,
          canLocate: here.supported,
          onLocate: locate,
          neighborhoods: city.file?.neighborhoods ?? [],
          counts: explore.counts.withinMinutes,
        }}
        sort={filters.sort}
        onFlipSort={() => void flipSort()}
        sortBusy={sortBusy}
        onShare={() => setShareOpen(true)}
        ask={glanceAsk}
        onAskDone={() => setGlanceAsk(null)}
      />
    );

  /** From 900px up: the right sidebar's top, or the right of the top strip when it is closed. */
  const siteLinks = (
    <>
      <TextLink onClick={() => setAboutOpen(true)}>About Us</TextLink>
      <TextLink onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}>
        Submit Feedback!
      </TextLink>
    </>
  );

  // Replaced by the one Account link below (D3): sign-up lives on AuthKit's
  // login page, and Log out on the Account page.
  // /**
  //  * Preview only: accounts are not live, and the pages say so. Always in the
  //  * top strip, so the right sidebar's two links keep the spacing they have in
  //  * production.
  //  */
  // const accountLinks = window.location.hostname === 'localhost' && (
  //   <PreviewLinks>
  //     {!user ? (
  //       <>
  //         <TextLink onClick={() => navigate(ACCOUNT_PATHS.logIn)}>Log in</TextLink>
  //         <TextLink onClick={() => navigate(ACCOUNT_PATHS.signUp)}>Sign up</TextLink>
  //       </>
  //     ) : (
  //       <>
  //         <TextLink onClick={() => {}}>{user.firstName ?? user.email}</TextLink>
  //         <TextLink onClick={() => signOut()}>Log out</TextLink>
  //       </>
  //     )}
  //   </PreviewLinks>
  // );

  /**
   * The city pill: the strip's right end from 900px up, the header's below.
   * Another city's distances mean nothing here, so a switch drops the band.
   */
  function cityPill(size: 'header' | 'phone') {
    return (
      <CityPill
        size={size}
        onSwitched={() => {
          setAddressOrigin(null);
          setFilters((f) => ({ ...f, withinMinutes: null, origin: 'home', sort: 'soonest' }));
        }}
      />
    );
  }

  /**
   * The callout, drawn once: in the right sidebar of either scope, in any
   * layout, where it is one, and otherwise over the page.
   */
  const callout = (
    <EventDetailSheet
      event={detail}
      layout={calloutLayout}
      past={detail ? isPastEvent(detail) : false}
      scheduled={detail ? rsvp.statusOf(detail.id) === 'going' : false}
      saved={detail ? rsvp.statusOf(detail.id) === 'maybe' : false}
      onClose={closeDetail}
      onBack={detailBack}
      onForward={detailForward}
      onToggleSchedule={(id) => toggleRsvp(id, 'going')}
      onToggleSave={(id) => toggleRsvp(id, 'maybe')}
      onReport={(e) => setReporting(e)}
      hidden={detail ? hidden.isHidden(detail.id) : false}
      edited={detail ? (edits.edits[detail.id]?.scope ?? null) : null}
      onToggleHidden={(id) => {
        // Hiding closes the callout, so the card can be seen leaving.
        if (!hidden.isHidden(id)) closeDetail();
        toggleHidden(id);
      }}
      onEdit={setEditing}
      onRevertEdit={revertEdit}
      onOpenEvent={showDetail}
      // Session 2: a series' dates, their RSVPs, and the shareable link.
      seriesDates={detail?.seriesId ? seriesDates(byId.values(), detail.seriesId, now) : undefined}
      statusOf={rsvp.statusOf}
      onCopyLink={copyLink}
      onGoToSchedule={() => {
        closeDetail();
        changeScope('saved');
      }}
    />
  );

  /**
   * With the callout beside the list, a click on the list's own ground — not a
   * card, not a control — closes it and puts the right sidebar back. The list
   * stays live for cards; its blank space is the way out.
   */
  const onListGround = (e: React.MouseEvent) => {
    if (rightHolds !== 'callout') return;
    if ((e.target as Element).closest('article, button, a, input, textarea, select, label, [role="button"], [role="radio"], [role="switch"]')) return;
    if (window.getSelection()?.toString()) return;
    closeDetail();
  };

  // --- what each scope draws, in each layout (contract §10) --------------------

  const cityShort = city.file?.cityShort ?? 'NYC';
  const countOf = (s: Scope) => scopes[s].cards.length;
  const eventsWord = (n: number) => `${n} ${n === 1 ? 'event' : 'events'}`;

  /**
   * The header's height below 900px, measured: the full-bleed map runs under
   * it, and keeps its controls and fits clear of it.
   */
  const [headerH, setHeaderH] = useState(0);
  useLayoutEffect(() => {
    const node = header.current;
    if (!node) return;
    const measure = () => setHeaderH(node.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isNarrow, loading]);
  /** The phone's band at the foot of the screen, which the map runs under too. */
  const bandPx = useMemo(() => (phoneChrome ? toggleBandPx() : 0), [phoneChrome]);
  /** The map runs full-bleed (contract §15): under the header or the top strip. */
  const fullBleed = layout === 'map' && !loading && !error;

  /** Saved's menu, as the Share my calendar dialog's overflow: the backup and its restore, Clear. */
  const savedMenu = (
    <SavedMenu file={city.file} byId={byId} rsvp={rsvp} hidden={hidden} round overflow notify={toasts.show} />
  );

  /**
   * The results bar: the heading and the count, in every layout. In the list
   * it is sticky, lined up with the tickets as it always was; over the
   * calendar it is a plain row; over the map, a chip floating under the top
   * strip. A phone has none. Saved's menu is in Share my calendar now.
   */
  const resultsBar = (s: Scope, where: Layout) => {
    if (phoneChrome) return null;
    const count = eventsWord(countOf(s)) + (withinText && ` ${withinText}`);
    return (
      <ResultsBar
        data-results-bar={where}
        sx={
          where === 'list'
            ? undefined
            : where === 'map'
              ? {
                  position: 'absolute',
                  top: isNarrow ? headerH + 6 : TOP_STRIP_H + 4,
                  left: isNarrow ? 16 : 52,
                  zIndex: 4,
                  maxWidth: 'calc(100% - 120px)',
                  p: '6px 16px',
                  borderRadius: '999px',
                  background: 'rgba(36,31,28,0.82)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                  flexDirection: 'row',
                }
              : // Clear of the two sidebar toggles in the column's top corners.
                { position: 'static', maxWidth: 'none', pl: { xs: 0, md: '20px' }, pr: { xs: 0, md: '20px' } }
        }
      >
        {/* The phone says the count in words and leaves the heading to
            the filter panel, which is already titled. */}
        {isMobile ? (
          <Typography sx={{ fontSize: 13, color: 'rgba(243,238,231,0.6)' }}>{count}</Typography>
        ) : (
          <Typography sx={{ fontFamily: FONTS.display, fontSize: 19, fontWeight: 700 }}>
            {s === 'saved' ? 'Saved' : activeFilterCount(filters) > 0 ? 'Search Results' : 'All Events'}
            <Box component="span" sx={{ fontSize: 12.5, opacity: 0.6, ml: 1.2, fontWeight: 600 }}>
              {count}
            </Box>
          </Typography>
        )}
      </ResultsBar>
    );
  };

  /** A phone's Saved, in the list and the calendar: Going and Maybe. Above the layout, not in it. */
  const phoneSavedBar = (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', p: '2px 12px 6px 16px' }}>
      <PhoneScheduleCount going={rsvp.goingIds.size} maybe={rsvp.maybeIds.size} />
    </Box>
  );

  /** Over a shared calendar: what it is, and the one thing to do with it. */
  const saveAllToMine = () => {
    if (!sharedList) return;
    const next = { ...rsvp.map };
    let added = 0;
    for (const [id, status] of sharedList) {
      if (next[id] || !city.byId.has(id)) continue;
      next[id] = status;
      added += 1;
    }
    rsvp.replace(next);
    toasts.show(added ? `Saved ${eventsWord(added)} to yours.` : 'You already have every one of these saved.');
    // On to one's own Saved, in the layout the shared one was drawn in. The
    // layout waits for the ids to leave the address: set in the same tap, the
    // filters' URL writer runs before the router commits and puts them back.
    changeScope('saved');
    setCalendarAfterShared(true);
  };
  const sharedBar = sharedList && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1.5,
        p: { xs: '4px 16px 10px', md: '8px 20px 6px' },
      }}
    >
      <Typography component="h2" sx={{ fontFamily: FONTS.serif, fontSize: { xs: 21, md: 24 }, fontWeight: 700 }}>
        Shared calendar
        <Box component="span" sx={{ fontFamily: FONTS.body, fontSize: 12.5, opacity: 0.6, ml: 1.2, fontWeight: 600 }}>
          {eventsWord(sharedList.size)}
        </Box>
      </Typography>
      <Button
        onClick={saveAllToMine}
        sx={{ background: BRAND.parchment, color: BRAND.mocha, borderRadius: 999, px: 2.5, fontWeight: 700, '&:hover': { background: '#fff' } }}
      >
        Save all to mine
      </Button>
    </Box>
  );

  /** The shifted-dates notice, for the layouts that have no sort. */
  const sampleNotice = (
    <ResultsNotes
      sort={filters.sort}
      canNearest={false}
      onSort={NOOP}
      shifted={Boolean(city.file?.datesShifted)}
      sortable={false}
    />
  );

  /** Saved with nothing saved: not "nothing matches", but where things come from. */
  const savedEmpty = (
    <Box sx={{ py: 10, textAlign: 'center', px: 3 }}>
      <Typography sx={{ fontFamily: FONTS.serif, fontSize: 24, fontWeight: 700, color: BRAND.parchment, mb: 1 }}>
        Nothing saved yet
      </Typography>
      <Typography sx={{ color: 'rgba(243,238,231,0.7)', fontSize: 14.5, mb: 2.5 }}>
        Mark events Going or Maybe in Explore and they land here.
      </Typography>
      <Button
        variant="outlined"
        onClick={() => changeScope('explore')}
        sx={{
          color: BRAND.parchment,
          borderColor: 'rgba(243,238,231,0.45)',
          borderRadius: 999,
          px: 2.5,
          '&:hover': { borderColor: BRAND.parchment, background: 'rgba(243,238,231,0.08)' },
        }}
      >
        Explore {cityShort}
      </Button>
    </Box>
  );

  /**
   * From 900px up, the sidebars' toggles, in the column's top corners in every
   * layout, and the view's own button immediately left of the right one
   * (contract §15). Over the full-bleed map they sit under the top strip.
   */
  const sidebarToggles = (top?: number) =>
    !isNarrow && (
      <>
        <ToggleSpot $side="left" style={{ top }}>
          <SidebarToggle side="left" open={filtersShown} what="filters" onToggle={toggleLeft} />
        </ToggleSpot>
        <ToggleSpot ref={keepResultsBarClear} $side="right" style={{ top, display: 'flex', alignItems: 'center', gap: 10 }}>
          {headerAction('desktop')}
          <SidebarToggle side="right" open={rightHolds !== null} what="panel" onToggle={toggleRight} />
        </ToggleSpot>
      </>
    );

  /** List: the tickets. Keyed by scope, so each keeps its own scroll. */
  const listColumn = (s: Scope) => (
    <PaneWrap key={`list:${s}`}>
      <Pane ref={listScroll[s].ref} onScroll={listScroll[s].onScroll}>
        <Centered>
          {resultsBar(s, 'list')}

          {/* The shifted-dates notice. The sort is the header's button now (contract §16). */}
          {sampleNotice}

          <EventList
            events={visible[s]}
            total={countOf(s)}
            ready
            generation={0}
            hasMore={visible[s].length < scopes[s].leads.length}
            hasPrevious={false}
            loadMore={loadMore}
            loadPrevious={NOOP}
            moreError={false}
            onRetry={NOOP}
            pane={listScroll[s].node}
            now={now}
            suggestion={scopes[s].suggestion}
            isScheduled={(id) => rsvp.statusOf(id) === 'going'}
            isSaved={(id) => rsvp.statusOf(id) === 'maybe'}
            savedView={s === 'saved'}
            onOpen={openDetail}
            onToggleSchedule={(id) => toggleRsvp(id, 'going')}
            onToggleSave={(id) => toggleRsvp(id, 'maybe')}
            onReport={setReporting}
            onFilterTopic={explore.toggleTopic}
            isHidden={hidden.isHidden}
            leavingIds={leaving}
            onToggleHidden={toggleHidden}
            onLoosen={explore.loosen}
            onResetAll={explore.resetAll}
            // Session 2: series dates and the under-18 warning.
            moreDates={scopes[s].moreDates}
            under18={filters.switches.has('under18')}
            swipeHint={s === 'explore' ? swipeHint : 0}
            emptyState={s === 'saved' && rsvp.allIds.size === 0 ? savedEmpty : undefined}
            travelFor={travelFor}
            unknownDistance={scopes[s].unknownLeads}
            groupOf={nearestGroups}
          />
        </Centered>
      </Pane>
      <Fade $edge="top" $phone={phoneChrome} />
      <Fade $edge="bottom" $phone={phoneChrome} />
      {sidebarToggles()}
    </PaneWrap>
  );

  /** Calendar: Session 3's month, fed every matching date rather than the RSVPs. */
  const calendarColumn = (s: Scope) => {
    // A shared calendar is someone else's list: its own tints, and nothing on it writes.
    const readOnly = s === 'saved' && sharedList;
    return (
    <PaneWrap key={`calendar:${s}${readOnly ? ':shared' : ''}`}>
      <CalendarView
        scope={s}
        events={scopes[s].dates}
        statusOf={readOnly ? (id) => sharedList.get(id) ?? null : rsvp.statusOf}
        now={now}
        activeId={detail?.id ?? null}
        focus={scheduleFocus}
        top={
          <>
            {readOnly ? sharedBar : resultsBar(s, 'calendar')}
            {sampleNotice}
          </>
        }
        cityShort={cityShort}
        suggestion={scopes[s].suggestion}
        onLoosen={explore.loosen}
        onExplore={() => changeScope('explore')}
        onOpen={openDetail}
        onToggleRsvp={readOnly ? () => toasts.show('This is a shared calendar. Save all to mine to keep these.') : toggleRsvp}
      />
      {sidebarToggles()}
    </PaneWrap>
    );
  };

  /**
   * Map: Leaflet, lazy-loaded, full-bleed (contract §15): it runs under the
   * header or the top strip and under a phone's band, told how far by its
   * insets, with MapChrome's gradients over the edges. A map keeps its own
   * drags, so below 900px the page swipe never starts on it. Below 900px both
   * scope pages are built, so only the one on screen gets a map: two would
   * fetch every tile twice.
   */
  const mapColumn = (s: Scope) => {
    const live = Boolean(city.file) && (!isNarrow || s === scope);
    return (
      <PaneWrap key={`map:${s}`} data-own-swipe style={{ flexDirection: 'column' }}>
        {resultsBar(s, 'map')}
        {city.file && live && (
          <Suspense fallback={null}>
            <MapView
              ref={mapHandle}
              cards={scopes[s].mapCards}
              origin={explore.origin}
              home={homeAt}
              device={here.point}
              mode={filters.mode}
              withinMinutes={filters.withinMinutes}
              shed={explore.travel.shed}
              city={city.file}
              rsvpOf={rsvp.statusOf}
              topInset={isNarrow ? headerH : TOP_STRIP_H + 44}
              bottomInset={bandPx}
              onOpen={(id) => {
                const event = byId.get(id);
                if (!event) return;
                showDetail(event);
                // A phone's callout comes half up, so the map it came from stays in view.
                setDetailHalf(isMobile);
              }}
              // The marker's popover is the picker; with no home yet there is no marker, so the panel is the way in.
              onEditHome={() => (isNarrow ? setFiltersOpen(true) : undefined)}
              onChangeMode={setMode}
              onChangeMinutes={explore.setWithin}
              renderHomePicker={(close) => (
                <HomePicker
                  neighborhoods={city.file?.neighborhoods ?? []}
                  home={homeStore.home}
                  autoFocus
                  onSetHome={(h) => {
                    setHome(h);
                    close();
                  }}
                  onCancel={homeStore.home ? close : undefined}
                  onUseCurrent={
                    here.supported && here.status !== 'ready'
                      ? () => {
                          close();
                          void locate();
                        }
                      : undefined
                  }
                  locating={here.status === 'locating'}
                />
              )}
            />
          </Suspense>
        )}
        {/* A phone's is drawn once, fixed beside the band's pill; everywhere else, in the map's own column. */}
        {live && !phoneChrome && <MapChrome variant="desktop" onRecenter={() => void recentre()} locating={recentring} />}
        {sidebarToggles(TOP_STRIP_H + 4)}
      </PaneWrap>
    );
  };

  /** A shared calendar is always a calendar: the layout pill is hidden over it. */
  const centre = (s: Scope) => {
    const l = s === 'saved' && sharedList ? 'calendar' : layout;
    return l === 'calendar' ? calendarColumn(s) : l === 'map' ? mapColumn(s) : listColumn(s);
  };

  /**
   * One page for either scope: from 900px up, the filters, the centre column
   * (its top strip, then the layout) and the right sidebar; below, the layout
   * alone, with Saved's slim bar over it on a phone. Everything around the
   * layout is the same in both scopes, so a switch changes only what is in it.
   */
  const scopePage = (s: Scope) => (
    // The full-bleed map runs to the screen's edges below 900px too.
    <Panes style={fullBleed && isNarrow ? { padding: 0 } : undefined}>
      {/* Drawn whenever the page is wide enough, and collapsed when stepped
          aside or closed, so it eases away rather than vanishing. */}
      {!isNarrow && (
        <ResizableSidebar
          scrollKey="explore:filters"
          flat
          collapsed={!filtersShown}
          fraction={leftFraction}
          maxFraction={maxLeftFraction}
          onResize={setLeftShare}
          onReset={() => setLeftShare(null)}
          top={lockupIn(LOCKUP_IN_SIDEBAR)}
        >
          {sidebar}
        </ResizableSidebar>
      )}

      {isNarrow ? (
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {phoneChrome && s === 'saved' && !sharedList && layout !== 'map' && phoneSavedBar}
          {centre(s)}
        </Box>
      ) : (
        <CentreColumn onClick={onListGround}>
          <TopStrip
            style={
              {
                '--strip-left': `${filtersEdge}px`,
                // Over the full-bleed map, which runs up under it.
                ...(fullBleed ? { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 4 } : null),
              } as CSSProperties
            }
          >
            <div>{!filtersShown && <StripLockup>{lockupIn(LOCKUP_IN_STRIP)}</StripLockup>}</div>
            <StripLinks>
              {/* Not while the callout is the right sidebar: it has no row for
                  them, and there is no room here (Sam, Sept 18). The city pill
                  is in the centred group now (contract §15). */}
              {!rightHolds && <StripSiteLinks>{siteLinks}</StripSiteLinks>}
            </StripLinks>
          </TopStrip>
          {centre(s)}
        </CentreColumn>
      )}

      {!isNarrow && (
        <RightColumn
          holds={rightHolds}
          panel={
            rightOpen && (
              <RightSidebar>
                <SidebarTop style={{ padding: `0 ${RIGHT_SIDEBAR_X}px` }}>{siteLinks}</SidebarTop>
                <RightSidebarBody />
              </RightSidebar>
            )
          }
          callout={calloutLayout === 'pane' ? callout : null}
        />
      )}
    </Panes>
  );

  return (
    <Shell
      style={
        {
          position: 'relative',
          '--toggle-x': `${toggleX}px`,
          '--toggle-w': `${toggleW}px`,
          '--toggle-l': `${span.left}px`,
          '--toggle-r': `${span.right}px`,
          // Only a phone has a band; everywhere else the fallbacks stand.
          ...(phoneChrome ? { '--toggle-band': TOGGLE_BAND, '--date-head-top': '0px' } : null),
        } as CSSProperties
      }
    >
      {/* From 900px up: the city pill, the layout pill and Saved, drawn once
          over the page and centred on the layout pill, so a switch never moves
          them out from under the pointer (contract §15). */}
      {!phoneChrome && !isNarrow && (
        <ToggleDock $over={false} style={{ left: toggleX - span.left }}>
          {chromeGroup}
        </ToggleDock>
      )}

      {/* A phone: the layout pill pinned to the bottom of the screen, outside
          PageSwipe (whose off-screen page is inert and whose strip is
          transformed while moving) and outside Header (which slides away under
          a half-up sheet); in Map layout, the recentre button beside it and
          the gradients over the map's edges. */}
      {phoneChrome && <PhoneToggleDock>{layoutPill('phone')}</PhoneToggleDock>}
      {phoneChrome && fullBleed && !sharedList && (
        <MapChrome variant="phone" onRecenter={() => void recentre()} locating={recentring} showRecentre={!detail} />
      )}

      {/* Below 900px only. From there up each column carries its own top. */}
      {isNarrow && (
        <Header
          ref={header}
          // The view's button takes what is left, and shrinks before it pushes the page wider.
          $template={phoneChrome ? 'auto auto auto minmax(0, 1fr)' : 'auto 1fr auto'}
          // Over the full-bleed map, which runs up under it.
          style={{
            // Four things across a 375px phone: 10px gaps leave "Sort by nearest" its whole width.
            ...(phoneChrome ? { columnGap: 10 } : null),
            ...(fullBleed ? { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 } : null),
          }}
        >
          <Box sx={{ display: 'flex', minWidth: 0 }}>
            <Lockup
              variant={isMobile || !headerWordmark ? 'phone' : 'narrow'}
              size={`${isMobile || !headerWordmark ? LOGO_W.phone : LOGO_W.narrow}px`}
              onHome={resetToHome}
              onAbout={() => setAboutOpen(true)}
            />
          </Box>

          {phoneChrome ? (
            /* The tree, the city pill, Saved, and the view's own button at the right edge. */
            <>
              {cityPill('phone')}
              {savedButton('phone')}
              <Box sx={{ justifySelf: 'end', minWidth: 0, maxWidth: '100%', display: 'flex' }}>{headerAction('phone')}</Box>
            </>
          ) : (
            <>
              {/* A window under 900px that is not a phone: the group keeps the
                  top, centred between the lockup and the view's button, or on
                  a row of its own where it does not fit beside them. */}
              <HeaderToggle style={stackedGroup ? { gridColumn: '1 / -1', gridRow: 2 } : undefined}>
                {chromeGroup}
              </HeaderToggle>
              <Box
                sx={{ justifySelf: 'end', display: 'flex', alignItems: 'center', minWidth: 0 }}
                style={stackedGroup ? { gridColumn: 3, gridRow: 1 } : undefined}
              >
                {headerAction('desktop')}
              </Box>
            </>
          )}
        </Header>
      )}

      <Content>
      {!isNarrow && (loading || error) && (
        <TopStrip>
          <div>{lockupIn(LOCKUP_IN_STRIP)}</div>
          <StripLinks>{siteLinks}</StripLinks>
        </TopStrip>
      )}

      {loading && (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress sx={{ color: BRAND.parchment }} />
        </Box>
      )}

      {error && !loading && (
        <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3, textAlign: 'center' }}>
          <Box>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, fontWeight: 700, mb: 1 }}>
              We couldn't load the events
            </Typography>
            <Typography sx={{ opacity: 0.7, fontSize: 14, mb: 2 }}>
              {error}. Check your connection and reload.
            </Typography>
            <Button
              onClick={() => window.location.reload()}
              sx={{ background: BRAND.parchment, color: BRAND.mocha, borderRadius: 999, px: 3, fontWeight: 700 }}
            >
              Reload
            </Button>
          </Box>
        </Box>
      )}

      {!loading && !error && isNarrow && (
        <>
          <PageSwipe
            ref={pages}
            view={scope}
            onView={changeScope}
            explore={scopePage('explore')}
            saved={savedReady ? scopePage('saved') : null}
            onExplorePointerDown={onExploreSwipe}
            accepts={swipeInput}
            onExploreOffset={NOOP}
          />
          <FilterPanel
            ref={filterPanel}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            // The filters belong to both scopes (contract §10).
            available
            shown={shownCount}
            total={poolCount}
            swipes
            mouseSwipes={isMobile}
          >
            {sidebar}
            {/* Account is the view toggle's third item now, on every layout. */}
            <TextLink
              onClick={() => window.open(FEEDBACK_URL, '_blank', 'noopener,noreferrer')}
              style={{ display: 'block', margin: '14px auto 0' }}
            >
              Submit Feedback!
            </TextLink>
          </FilterPanel>
        </>
      )}

      {!loading && !error && !isNarrow && scopePage(scope)}

      {calloutLayout !== 'pane' && callout}

      <ToastBar
        toast={toasts.toast}
        onUndo={NOOP}
        onUnhide={(id) => {
          toasts.dismiss();
          unhideEvent(id);
        }}
        onGoToSchedule={(_id, dayKey) => {
          toasts.dismiss();
          setScheduleFocus((f) => ({ dayKey, n: (f?.n ?? 0) + 1 }));
          changeScope('saved');
        }}
      />

      <ShareCalendarDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        file={city.file}
        byId={byId}
        saved={rsvp.map}
        notify={toasts.show}
        overflow={savedMenu}
      />
      <ReportDialog event={reporting} onClose={() => setReporting(null)} correctionsEmail={city.file?.correctionsEmail} />
      <EventEditDialog
        event={editing}
        formats={formats}
        onClose={() => setEditing(null)}
        onSave={saveEdit}
      />

      <MobileNotice open={mobileNotice.open && cityChosen} onDismiss={mobileNotice.dismiss} />
      <AboutModal
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        correctionsEmail={city.file?.correctionsEmail}
      />
      <CityChooser />
      </Content>
    </Shell>
  );
}
